import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Save, Trophy, CalendarRange, ArrowRight, KeySquare, Archive, AlertTriangle } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import { Field, GIORNI_SETTIMANA, blurSuTouch } from "@/components/Shared";
import { useRefreshOnPull } from "@/components/PullToRefresh";
import { Switch } from "@/components/ui/switch";
import { useAuth } from "@/context/AuthContext";

export default function Impostazioni() {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  const { user } = useAuth();
  // Stagione: la lettura è per Responsabili e Admin, l'apertura di una nuova
  // solo per l'Admin. È un'azione che cambia come vengono marcati TUTTI i
  // documenti creati da qui in avanti, quindi non deve essere a portata di
  // mano di chiunque entri nelle impostazioni.
  const [stagione, setStagione] = useState(null);
  // Permessi granulari: solo l'admin li vede e li assegna
  const [permessi, setPermessi] = useState(null);
  const [permSaving, setPermSaving] = useState(null);
  const [nuovaStagione, setNuovaStagione] = useState("");
  const [apriOpen, setApriOpen] = useState(false);
  const [apreStagione, setApreStagione] = useState(false);

  // Amministratori e detentore dichiarato dell'account di sistema (lotto 9.3)
  const [admin, setAdmin] = useState(null);
  const [detentore, setDetentore] = useState("");
  const [detentoreOpen, setDetentoreOpen] = useState(false);

  const carica = () => {
    api.get("/impostazioni").then((r) => setS(r.data));
    api.get("/stagione").then((r) => setStagione(r.data)).catch(() => setStagione(null));
    api.get("/permessi").then((r) => setPermessi(r.data)).catch(() => setPermessi(null));
    api.get("/amministratori").then((r) => {
      setAdmin(r.data);
      setDetentore(r.data?.detentore?.nome || "");
    }).catch(() => setAdmin(null));
  };

  const salvaDetentore = async () => {
    const v = detentore.trim();
    if (v.length < 2) return toast.error("Indica nome e cognome di chi detiene l'account");
    try {
      await api.put("/amministratori/detentore", { nome: v });
      toast.success("Detentore dichiarato");
      setDetentoreOpen(false);
      carica();
    } catch (err) { toast.error(errMsg(err)); }
  };

  const togglePermesso = async (chiave, uid) => {
    const p = permessi.permessi.find((x) => x.chiave === chiave);
    const nuovi = p.utenti_ids.includes(uid)
      ? p.utenti_ids.filter((x) => x !== uid)
      : [...p.utenti_ids, uid];
    setPermSaving(chiave);
    try {
      const { data } = await api.put(`/permessi/${chiave}`, { utenti_ids: nuovi });
      setPermessi((x) => ({
        ...x,
        permessi: x.permessi.map((q) => (q.chiave === chiave ? { ...q, utenti_ids: data.utenti_ids } : q)),
      }));
      toast.success("Permesso aggiornato");
    } catch (err) { toast.error(errMsg(err)); }
    finally { setPermSaving(null); }
  };

  // Manutenzione dei periodi di conservazione. Parte SEMPRE in simulazione:
  // il pulsante che cancella davvero compare solo dopo aver visto i numeri.
  const [verbale, setVerbale] = useState(null);
  const [manutInCorso, setManutInCorso] = useState(false);
  const [manutConferma, setManutConferma] = useState(false);

  const eseguiManutenzione = async (dryRun) => {
    setManutInCorso(true);
    try {
      const { data } = await api.post(`/manutenzione/chiusura-stagione?dry_run=${dryRun}`);
      setVerbale(data);
      setManutConferma(false);
      if (dryRun) toast.success("Simulazione completata: non è stato cancellato nulla");
      else { toast.success("Manutenzione eseguita"); carica(); }
    } catch (err) { toast.error(errMsg(err)); }
    finally { setManutInCorso(false); }
  };

  // I conteggi arrivano annidati per collezione: qui si sommano solo per
  // mostrare le righe di riepilogo. Il dettaglio resta nel verbale.
  const sommaSu = (oggetto, campo) =>
    Object.values(oggetto || {})
      .filter((v) => v && typeof v === "object" && !Array.isArray(v))
      .reduce((tot, v) => tot + (v[campo] || 0), 0);

  const apriNuovaStagione = async () => {
    const v = nuovaStagione.trim();
    if (!/^\d{4}(\/\d{2,4})?$/.test(v)) return toast.error("Formato non valido: usa «2027» oppure «2027/28»");
    setApreStagione(true);
    try {
      const { data } = await api.put("/stagione", { stagione: v });
      if (data.invariata) toast.info("È già la stagione corrente");
      else toast.success(`Nuova stagione aperta: ${data.stagione_corrente}`);
      setApriOpen(false); setNuovaStagione(""); carica();
    } catch (err) { toast.error(errMsg(err)); }
    finally { setApreStagione(false); }
  };

  useEffect(() => { carica(); }, []);
  useRefreshOnPull(carica);

  if (!s) return <p className="text-slate-400 font-semibold">Caricamento...</p>;

  const toggleGiorno = (i) =>
    setS((x) => ({ ...x, giorni_attivi: x.giorni_attivi.includes(i) ? x.giorni_attivi.filter((g) => g !== i) : [...x.giorni_attivi, i].sort() }));

  const salva = async () => {
    if (!s.periodo_inizio || !s.periodo_fine) return toast.error("Indica prima il periodo dell'oratorio");
    if (s.periodo_inizio > s.periodo_fine) return toast.error("La data di inizio non può essere successiva alla data di fine");
    if (!s.giorni_attivi?.length) return toast.error("Seleziona almeno un giorno attivo");
    setSaving(true);
    try {
      const { data } = await api.put("/impostazioni", {
        periodo_inizio: s.periodo_inizio,
        periodo_fine: s.periodo_fine,
        giorni_attivi: s.giorni_attivi,
        max_animati: Number(s.max_animati),
        iscrizioni_aperte: s.iscrizioni_aperte,
        max_tentativi_login: Number(s.max_tentativi_login),
        blocco_minuti: Number(s.blocco_minuti),
      });
      setS(data);
      toast.success("Impostazioni salvate");
    } catch (err) { toast.error(errMsg(err)); }
    finally { setSaving(false); }
  };

  return (
    <div data-testid="impostazioni-page" className="max-w-3xl">
      <h1 className="font-display text-2xl md:text-3xl font-extrabold text-slate-900 mb-1">Impostazioni</h1>
      <p className="text-sm text-slate-500 font-semibold mb-6">Parametri della stagione</p>

      {/* --- STAGIONE --- */}
      {stagione && (
        <div className="card-app p-6 mb-4" data-testid="stagione-card">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <p className="flex items-center gap-2 font-display font-extrabold text-slate-900">
                <CalendarRange className="h-5 w-5 text-amber-500" /> Stagione corrente
              </p>
              <p className="font-display text-3xl font-extrabold text-slate-900 mt-1" data-testid="stagione-corrente">
                {stagione.stagione_corrente}
              </p>
              <p className="text-xs text-slate-500 font-semibold mt-1">
                Ogni iscrizione, presenza, squadra e gruppo creato da ora nasce marcato con
                questa stagione. È ciò che permette, in futuro, di separare o cancellare
                un'annata intera senza toccare le altre.
              </p>
            </div>
            {user?.role === "admin" && !apriOpen && (
              <button type="button" className="btn-ghost !py-2 text-xs shrink-0"
                onClick={() => { setApriOpen(true); setNuovaStagione(String(Number(String(stagione.stagione_corrente).slice(0, 4)) + 1)); }}
                data-testid="apri-stagione-btn">
                <ArrowRight className="h-3.5 w-3.5" /> Apri nuova stagione
              </button>
            )}
          </div>

          {apriOpen && (
            <div className="rounded-2xl bg-amber-50 border border-amber-200 p-4 mt-4" data-testid="apri-stagione-form">
              <p className="text-sm font-bold text-slate-800 mb-1">Aprire una nuova stagione</p>
              <p className="text-xs text-slate-700 font-medium leading-relaxed mb-3">
                <b>Non cancella e non sposta nulla.</b> Cambia solo il valore con cui verranno
                marcati i documenti creati da qui in avanti: i dati della stagione precedente
                restano dove sono, distinguibili dal loro contrassegno.
              </p>
              <div className="flex gap-2 flex-wrap items-center">
                <input className="input-app !py-2 max-w-[160px]" value={nuovaStagione}
                  onChange={(e) => setNuovaStagione(e.target.value)}
                  placeholder="2027" data-testid="nuova-stagione-input" />
                <button type="button" className="btn-primary !py-2 text-xs"
                  onClick={apriNuovaStagione} disabled={apreStagione} data-testid="apri-stagione-conferma">
                  {apreStagione ? "Apertura..." : "Apri"}
                </button>
                <button type="button" className="btn-ghost !py-2 text-xs"
                  onClick={() => setApriOpen(false)} data-testid="apri-stagione-annulla">Annulla</button>
              </div>
            </div>
          )}

          {/* Conteggi: dicono a colpo d'occhio quanto della stagione corrente è
              già popolato, e quanto resta nelle annate precedenti. */}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-4 pt-4 border-t border-slate-100">
            {Object.entries(stagione.collezioni || {})
              .filter(([, c]) => c.totale > 0)
              .map(([nome, c]) => (
                <div key={nome} className="rounded-xl bg-slate-50 px-3 py-2" data-testid={`stagione-conteggio-${nome}`}>
                  <p className="text-[11px] font-bold text-slate-500 uppercase tracking-wide truncate">{nome}</p>
                  <p className="text-sm font-bold text-slate-800">
                    {c.corrente}
                    {c.totale !== c.corrente && (
                      <span className="text-slate-400 font-semibold"> di {c.totale}</span>
                    )}
                  </p>
                </div>
              ))}
            {Object.values(stagione.collezioni || {}).every((c) => c.totale === 0) && (
              <p className="text-xs text-slate-400 font-semibold col-span-full">
                Nessun dato ancora registrato in questa stagione.
              </p>
            )}
          </div>

          {/* Le date di chiusura sono l'ancora di ogni periodo di conservazione:
              «12 mesi dalla fine della stagione» si conta da qui. Vanno mostrate,
              altrimenti nessuno può accorgersi che ne manca una. */}
          {Object.keys(stagione.stagioni_chiuse || {}).length > 0 && (
            <div className="mt-3 pt-3 border-t border-slate-100" data-testid="stagioni-chiuse">
              <p className="text-[11px] font-bold text-slate-500 uppercase tracking-wide mb-1">
                Stagioni chiuse
              </p>
              <p className="text-xs text-slate-600 font-semibold">
                {Object.entries(stagione.stagioni_chiuse)
                  .sort(([a], [b]) => b.localeCompare(a))
                  .map(([s, quando]) => `${s} (${new Date(quando).toLocaleDateString("it-IT")})`)
                  .join(" · ")}
              </p>
            </div>
          )}
        </div>
      )}

      {/* --- AMMINISTRATORI (solo admin) ---
          Un account condiviso fra due persone non è un problema di sicurezza:
          è un problema di imputabilità. Il registro continua a scrivere, ma
          «l'Amministratore ha fatto X» smette di identificare qualcuno. */}
      {user?.role === "admin" && admin && (
        <div className="card-app p-6 mb-4" data-testid="amministratori-card">
          <p className="flex items-center gap-2 font-display font-extrabold text-slate-900">
            <KeySquare className="h-5 w-5 text-amber-500" /> Amministratori
          </p>
          <p className="text-xs text-slate-500 font-semibold mt-1 leading-relaxed">
            Se più persone usano lo stesso account, il registro non dice più chi ha fatto
            cosa. Crea un account intestato a ciascuno da Animatori → nuovo utente.
          </p>

          <div className="mt-4 space-y-2">
            {admin.amministratori.map((a) => (
              <div key={a.id} className="rounded-2xl border border-slate-200 p-3"
                data-testid={`amministratore-${a.id}`}>
                <p className="text-sm font-bold text-slate-800">
                  {a.nome} {a.cognome}
                  <span className="text-xs font-semibold text-slate-500 ml-2">@{a.username}</span>
                </p>
                <p className="text-xs font-semibold text-slate-500">
                  {a.di_sistema ? "Account di sistema · non eliminabile" : "Account nominativo"}
                  {!a.attivo && " · non ancora attivato"}
                </p>
              </div>
            ))}
          </div>

          <div className="rounded-2xl bg-slate-50 border border-slate-200 p-4 mt-4">
            <p className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-1">
              Chi detiene l'account di sistema
            </p>
            {admin.detentore?.nome && !detentoreOpen ? (
              <p className="text-sm font-bold text-slate-800">
                {admin.detentore.nome}
                <span className="text-xs font-semibold text-slate-500 ml-2">
                  dichiarato il {new Date(admin.detentore.dichiarato_il).toLocaleDateString("it-IT")}
                </span>
                <button type="button" className="btn-ghost !py-1 text-[11px] ml-2"
                  onClick={() => setDetentoreOpen(true)} data-testid="detentore-modifica">
                  Modifica
                </button>
              </p>
            ) : (
              <div className="flex gap-2 flex-wrap items-center">
                <input className="input-app !py-2 max-w-[16rem]" value={detentore}
                  onChange={(e) => setDetentore(e.target.value)}
                  placeholder="Nome e cognome" data-testid="detentore-input" />
                <button type="button" className="btn-primary !py-2 text-xs"
                  onClick={salvaDetentore} data-testid="detentore-salva">Dichiara</button>
              </div>
            )}
            <p className="text-[11px] text-slate-500 font-semibold mt-2 leading-snug">
              È una dichiarazione, non un controllo: serve a poter dire, fra un anno,
              chi rispondeva di questo account.
            </p>
          </div>
        </div>
      )}

      {/* --- MANUTENZIONE: CONSERVAZIONE DEI DATI (solo admin) ---
          I dati non si conservano per sempre: ogni categoria ha un periodo
          approvato, e questo è il comando che lo attua. Parte in simulazione,
          e il pulsante che cancella davvero appare solo dopo. */}
      {user?.role === "admin" && (
        <div className="card-app p-6 mb-4" data-testid="manutenzione-card">
          <p className="flex items-center gap-2 font-display font-extrabold text-slate-900">
            <Archive className="h-5 w-5 text-amber-500" /> Conservazione dei dati
          </p>
          <p className="text-xs text-slate-500 font-semibold mt-1 leading-relaxed">
            Attua i periodi di conservazione: cancella le informazioni sanitarie scadute,
            programma la rimozione dei dati delle stagioni chiuse, elimina dal server i
            file degli allegati scaduti e mette al sicuro per 5 anni la prova dei consensi.
            <b> La simulazione non cancella nulla</b>: serve a vedere i numeri prima di decidere.
          </p>

          <div className="flex gap-2 flex-wrap mt-4">
            <button type="button" className="btn-ghost !py-2 text-xs" disabled={manutInCorso}
              onClick={() => eseguiManutenzione(true)} data-testid="manutenzione-simula">
              {manutInCorso ? "Analisi..." : "Simula"}
            </button>
            {verbale?.dry_run && !manutConferma && (
              <button type="button" className="btn-ghost !py-2 text-xs !text-red-600"
                onClick={() => setManutConferma(true)} data-testid="manutenzione-esegui">
                Esegui davvero
              </button>
            )}
          </div>

          {manutConferma && (
            <div className="rounded-2xl bg-red-50 border border-red-200 p-4 mt-3" data-testid="manutenzione-conferma">
              <p className="flex items-center gap-2 text-sm font-bold text-red-800 mb-1">
                <AlertTriangle className="h-4 w-4" /> L'operazione non si può annullare
              </p>
              <p className="text-xs text-slate-700 font-medium leading-relaxed mb-3">
                I dati scaduti verranno cancellati per davvero, e i file rimossi dal server.
                Non esistono copie da cui recuperarli. Controlla i numeri della simulazione
                qui sotto prima di procedere.
              </p>
              <div className="flex gap-2 flex-wrap">
                <button type="button" className="btn-primary !py-2 text-xs !bg-red-600" disabled={manutInCorso}
                  onClick={() => eseguiManutenzione(false)} data-testid="manutenzione-conferma-si">
                  {manutInCorso ? "Esecuzione..." : "Sì, cancella"}
                </button>
                <button type="button" className="btn-ghost !py-2 text-xs"
                  onClick={() => setManutConferma(false)} data-testid="manutenzione-annulla">Annulla</button>
              </div>
            </div>
          )}

          {verbale && (
            <div className="mt-4 pt-4 border-t border-slate-100" data-testid="manutenzione-verbale">
              <p className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-2">
                {verbale.dry_run ? "Simulazione" : "Esecuzione reale"} ·{" "}
                {new Date(verbale.eseguito_il).toLocaleString("it-IT")}
              </p>
              <div className="grid grid-cols-2 gap-2">
                {[
                  ["Documenti con scadenza programmata",
                    sommaSu(verbale.conteggi?.stagionali, "datati") + sommaSu(verbale.conteggi?.data_propria, "datati")],
                  ["Informazioni sanitarie cancellate", sommaSu(verbale.conteggi?.campi_sanitari, "cancellati")],
                  ["File rimossi dal server", verbale.conteggi?.file_allegati?.file_rimossi || 0],
                  ["Consensi messi in archivio", sommaSu(verbale.conteggi?.stagionali, "consensi_archiviati")],
                  ["Account scaduti", verbale.conteggi?.account?.da_cancellare || 0],
                  ["File orfani sul server",
                    (verbale.conteggi?.file_orfani?.allegati_programma?.orfani_su_disco || 0)
                    + (verbale.conteggi?.file_orfani?.autorizzazioni?.orfani_su_disco || 0)],
                ].map(([etichetta, valore]) => (
                  <div key={etichetta} className="rounded-xl bg-slate-50 px-3 py-2">
                    <p className="text-[11px] font-bold text-slate-500 leading-tight">{etichetta}</p>
                    <p className="text-sm font-bold text-slate-800">{valore}</p>
                  </div>
                ))}
              </div>

              {/* Una stagione chiusa prima che l'app registrasse la data di
                  chiusura viene valutata su una stima, e va detto. */}
              {verbale.conteggi?.stagionali?._stagioni_stimate?.length > 0 && (
                <p className="text-xs text-amber-700 font-semibold mt-3 leading-relaxed" data-testid="manutenzione-stime">
                  Per {verbale.conteggi.stagionali._stagioni_stimate.join(", ")} non è registrata
                  la data di chiusura: è stata stimata al 31 dicembre. La stima conserva i dati
                  qualche mese in più del dovuto, mai di meno.
                </p>
              )}
              {verbale.conteggi?.account?.da_cancellare > 0 && !verbale.conteggi?.account?.interruttore && (
                <p className="text-xs text-slate-500 font-semibold mt-2 leading-relaxed">
                  Gli account scaduti sono contati ma non vengono cancellati: la cancellazione
                  degli account va abilitata a parte sul server.
                </p>
              )}
            </div>
          )}
        </div>
      )}

      {/* --- PERMESSI GRANULARI (solo admin) --- */}
      {user?.role === "admin" && permessi && (
        <div className="card-app p-6 mb-4" data-testid="permessi-card">
          <p className="flex items-center gap-2 font-display font-extrabold text-slate-900 mb-1">
            <KeySquare className="h-5 w-5 text-amber-500" /> Permessi dei Responsabili
          </p>
          <p className="text-xs text-slate-500 font-semibold mb-4">
            Essere Responsabile non basta per vedere tutto. Queste due categorie di dati si
            assegnano una persona alla volta: chi non è selezionato non le riceve nemmeno
            dall'API. Tu, come Amministratore, le vedi sempre.
          </p>

          {permessi.responsabili.length === 0 ? (
            <p className="text-xs text-slate-400 font-semibold">
              Nessun Responsabile registrato: non c'è ancora nessuno a cui assegnare i permessi.
            </p>
          ) : (
            <div className="space-y-4">
              {permessi.permessi.map((p) => (
                <div key={p.chiave} className="rounded-2xl border border-slate-200 p-3" data-testid={`permesso-${p.chiave}`}>
                  <p className="text-sm font-bold text-slate-800">{p.etichetta}</p>
                  <p className="text-xs text-slate-500 font-semibold mb-2.5">{p.descrizione}</p>
                  <div className="flex flex-wrap gap-2">
                    {permessi.responsabili.map((r) => {
                      const attivo = p.utenti_ids.includes(r.id);
                      return (
                        <button key={r.id} type="button"
                          onClick={() => togglePermesso(p.chiave, r.id)}
                          disabled={permSaving === p.chiave}
                          className={`rounded-full px-3.5 py-1.5 text-xs font-bold border transition-colors disabled:opacity-50 ${
                            attivo ? "bg-emerald-500 text-white border-emerald-500"
                                   : "bg-white border-slate-200 text-slate-600 hover:border-amber-400"}`}
                          data-testid={`permesso-${p.chiave}-${r.id}`}>
                          {r.nome} {r.cognome}
                        </button>
                      );
                    })}
                  </div>
                  {p.utenti_ids.length === 0 && (
                    <p className="text-[11px] text-amber-700 font-semibold mt-2">
                      Nessuno selezionato: al momento questi dati li vedi solo tu.
                    </p>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="card-app p-6 mb-4">
        <Field label="Periodo dell'oratorio" hint="Le giornate operative sono valide solo tra queste due date, comprese">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="label-app">Inizio</label>
              <input type="date" className="input-app" value={s.periodo_inizio || ""}
                onChange={(e) => setS({ ...s, periodo_inizio: e.target.value })} data-testid="periodo-inizio-input" />
            </div>
            <div>
              <label className="label-app">Fine</label>
              <input type="date" className="input-app" min={s.periodo_inizio || undefined} value={s.periodo_fine || ""}
                onChange={(e) => setS({ ...s, periodo_fine: e.target.value })} data-testid="periodo-fine-input" />
            </div>
          </div>
        </Field>

        <Field label="Giorni attivi dell'oratorio" hint="Presenze, programma, postazioni e punteggi sono operativi solo in questi giorni e nel periodo sopra indicato">
          <div className="flex gap-2 flex-wrap">
            {GIORNI_SETTIMANA.map((g, i) => (
              <button key={g} type="button" onClick={() => toggleGiorno(i)} disabled={!s.periodo_inizio || !s.periodo_fine}
                className={`rounded-full px-4 py-2 text-sm font-bold transition-colors disabled:cursor-not-allowed disabled:opacity-40 ${s.giorni_attivi.includes(i) ? "bg-amber-500 text-white shadow-sm" : "bg-slate-100 text-slate-400"}`}
                data-testid={`giorno-attivo-${i}`}>
                {g}
              </button>
            ))}
          </div>
        </Field>

        <Field label="Numero massimo di Animati" hint="Al raggiungimento, tutte le iscrizioni si bloccano automaticamente">
          <input type="number" min="1" className="input-app max-w-[160px]" value={s.max_animati} onChange={(e) => setS({ ...s, max_animati: e.target.value })} data-testid="max-animati-input" />
        </Field>

        <div className="flex items-center justify-between rounded-2xl bg-slate-50 p-4 mb-4">
          <div>
            <p className="text-sm font-bold text-slate-700">Iscrizioni pubbliche aperte</p>
            <p className="text-xs text-slate-400">Controlla solo il link pubblico; la Segreteria può sempre iscrivere</p>
          </div>
          <Switch checked={s.iscrizioni_aperte} onCheckedChange={(v) => setS({ ...s, iscrizioni_aperte: v })} data-testid="impostazioni-iscrizioni-switch" />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Tentativi login prima del blocco">
            <input type="number" min="1" className="input-app" value={s.max_tentativi_login} onChange={(e) => setS({ ...s, max_tentativi_login: e.target.value })} data-testid="max-tentativi-input" />
          </Field>
          <Field label="Durata blocco (minuti)">
            <input type="number" min="1" className="input-app" value={s.blocco_minuti} onChange={(e) => setS({ ...s, blocco_minuti: e.target.value })} data-testid="blocco-minuti-input" />
          </Field>
        </div>
      </div>

      <button className="btn-primary" disabled={saving} onClick={salva} data-testid="impostazioni-salva-btn">
        <Save className="h-4 w-4" /> {saving ? "Salvataggio..." : "Salva impostazioni"}
      </button>
    </div>
  );
}

/**
 * Card "Visibilità classifica": l'admin designa l'UNICO Responsabile che può
 * vedere la classifica in anteprima. Si auto-nasconde se chi guarda non è admin
 * (il backend risponde puoi_designare=false).
 */
export function ClassificaVisibilita() {
  const [stato, setStato] = useState(null);
  const [responsabili, setResponsabili] = useState([]);
  const [salvando, setSalvando] = useState(false);
  const [aperta, setAperta] = useState(false);

  const carica = () => {
    api.get("/classifica/stato").then((r) => setStato(r.data)).catch(() => {});
  };
  useEffect(() => {
    carica();
    api.get("/utenti?role=responsabile").then((r) => setResponsabili(r.data)).catch(() => {});
  }, []);

  if (!stato || !stato.puoi_designare) return null;

  const cambiaDesignato = async (id) => {
    setSalvando(true);
    try {
      await api.put("/classifica/designato", { responsabile_id: id || null });
      toast.success(id ? "Designato aggiornato" : "Nessun designato: solo l'admin vede l'anteprima");
      carica();
    } catch (err) { toast.error(errMsg(err)); }
    finally { setSalvando(false); }
  };

  return (
    <div className="card-app p-6 mb-4" data-testid="classifica-visibilita">
      <button type="button" className="w-full flex items-center justify-between gap-3 text-left" onClick={() => setAperta((v) => !v)} aria-expanded={aperta}>
        <span className="font-display font-extrabold text-slate-900 flex items-center gap-2">
          <Trophy className="h-4 w-4 text-emerald-500" /> Visibilità classifica
        </span>
        <span className="text-xs font-bold text-slate-400">{aperta ? "Chiudi" : "Apri"}</span>
      </button>

      {aperta && (
        <div className="mt-4">
          <p className="text-xs text-slate-400 font-semibold mb-4">
            Gli animatori non vedono la classifica finché non viene resa pubblica. Puoi incaricare
            un Responsabile di vederla in anteprima e decidere quando pubblicarla.
          </p>

          <Field label="Responsabile incaricato" hint="Uno solo alla volta: sceglierne un altro sostituisce il precedente">
            <select className="input-app" value={stato.designato?.id || ""} disabled={salvando}
              onChange={(e) => { cambiaDesignato(e.target.value); blurSuTouch(e); }}
              data-testid="classifica-designato-select">
              <option value="">Nessuno (solo l'Amministratore)</option>
              {responsabili.map((r) => (
                <option key={r.id} value={r.id}>{r.nome} {r.cognome}</option>
              ))}
            </select>
          </Field>

          <div className={`rounded-2xl p-3 text-xs font-bold flex items-center gap-2 ${stato.pubblica ? "bg-emerald-50 text-emerald-700" : "bg-slate-50 text-slate-500"}`}>
            <span className={`h-2 w-2 rounded-full ${stato.pubblica ? "bg-emerald-500" : "bg-slate-400"}`} />
            {stato.pubblica
              ? "La classifica è attualmente pubblica: la vedono tutti."
              : "La classifica non è ancora pubblica. La pubblica l'incaricato (o tu) dalla sezione Classifica."}
          </div>
        </div>
      )}
    </div>
  );
}
